import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  ArrowLeft,
  Plus,
  Search,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Download,
  BookOpen,
  Globe,
  Mail,
  Phone,
  Building2,
  KeyRound,
  Star,
  ThumbsUp,
  ThumbsDown,
  Award,
  X,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RATING_OPTIONS = [
  { value: 'excellent', label: 'Eccellente', color: 'text-amber-600', bg: 'bg-amber-100', icon: Award },
  { value: 'good', label: 'Buono', color: 'text-emerald-600', bg: 'bg-emerald-100', icon: ThumbsUp },
  { value: 'avoid', label: 'Da evitare', color: 'text-red-600', bg: 'bg-red-100', icon: ThumbsDown },
];

const RATING_MAP = Object.fromEntries(RATING_OPTIONS.map((r) => [r.value, r]));

const EMPTY_FORM = {
  name: '',
  service_type: '',
  service_categories: [],
  website: '',
  destinations: '',
  agency_code: '',
  username: '',
  password: '',
  booking_contact_1: '',
  booking_contact_2: '',
  booking_email: '',
  to_email: '',
  notes: '',
};

const FIELD_LABELS = {
  name: 'Nome Tour Operator',
  service_type: 'Tipologia Servizio',
  website: 'Sito Internet',
  destinations: 'Destinazioni Coperte',
  agency_code: 'Codice Agenzia',
  username: 'User',
  password: 'Password',
  booking_contact_1: 'Contatto Booking T.O. (1)',
  booking_contact_2: 'Contatto Booking T.O. (2)',
  booking_email: 'Email Booking',
  to_email: 'Email T.O.',
  notes: 'Note',
};

const TourOperators = () => {
  const { user } = useAuth();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [revealedPasswords, setRevealedPasswords] = useState({});
  const [predefinedCategories, setPredefinedCategories] = useState([]);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [byCategory, setByCategory] = useState([]);
  const [activeTab, setActiveTab] = useState('rubrica');

  useEffect(() => {
    fetchOperators();
    fetchPredefinedCategories();
  }, []);

  useEffect(() => {
    if (activeTab === 'ranking') {
      fetchByCategory();
    }
  }, [activeTab]);

  const fetchPredefinedCategories = async () => {
    try {
      const res = await axios.get(`${API}/tour-operators/categories/predefined`);
      setPredefinedCategories(res.data?.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchByCategory = async () => {
    try {
      const res = await axios.get(`${API}/tour-operators/by-category`);
      setByCategory(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento del ranking');
    }
  };

  const fetchOperators = async (query = '') => {
    setLoading(true);
    try {
      const url = query ? `${API}/tour-operators?search=${encodeURIComponent(query)}` : `${API}/tour-operators`;
      const res = await axios.get(url);
      setOperators(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Errore nel caricamento dei Tour Operator");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOperators(search.trim());
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setNewCategoryInput('');
    setShowForm(true);
  };

  const openEdit = (op) => {
    setEditingId(op.id);
    setForm({ ...EMPTY_FORM, ...op, service_categories: op.service_categories || [] });
    setNewCategoryInput('');
    setShowForm(true);
  };

  // ---- Category helpers ----
  const addCategory = (categoryName) => {
    const c = (categoryName || '').trim();
    if (!c) return;
    const existing = (form.service_categories || []).some(
      (x) => x.category.toLowerCase() === c.toLowerCase()
    );
    if (existing) {
      toast.error('Categoria già presente');
      return;
    }
    setForm({
      ...form,
      service_categories: [...(form.service_categories || []), { category: c, rating: 'good' }],
    });
    setNewCategoryInput('');
  };

  const updateCategoryRating = (index, rating) => {
    const updated = [...(form.service_categories || [])];
    updated[index] = { ...updated[index], rating };
    setForm({ ...form, service_categories: updated });
  };

  const removeCategory = (index) => {
    const updated = (form.service_categories || []).filter((_, i) => i !== index);
    setForm({ ...form, service_categories: updated });
  };

  const availablePredefined = predefinedCategories.filter(
    (c) => !(form.service_categories || []).some((x) => x.category.toLowerCase() === c.toLowerCase())
  );

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Il Nome Tour Operator è obbligatorio');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/tour-operators/${editingId}`, form);
        toast.success('Tour Operator aggiornato');
      } else {
        await axios.post(`${API}/tour-operators`, form);
        toast.success('Tour Operator creato');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      fetchOperators(search.trim());
      if (activeTab === 'ranking') fetchByCategory();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/tour-operators/${deleteId}`);
      toast.success('Tour Operator eliminato');
      setDeleteId(null);
      fetchOperators(search.trim());
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'eliminazione');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/tour-operators/export/excel`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rubrica_tour_operators_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel scaricato');
    } catch (err) {
      console.error(err);
      toast.error('Errore durante l\'export');
    }
  };

  const togglePassword = (id) => {
    setRevealedPasswords((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Accesso negato</CardTitle>
            <CardDescription>
              Solo gli amministratori possono accedere alla Rubrica Tour Operator.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" data-testid="tour-operators-page">
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard">
                <Button variant="outline" size="sm" data-testid="back-to-dashboard-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Dashboard
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-blue-600" />
                <h1 className="text-lg sm:text-xl font-bold text-slate-800">Rubrica Tour Operator</h1>
              </div>
            </div>
            <Badge variant="secondary" className="bg-red-100 text-red-800">Admin</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full sm:w-auto grid-cols-2 sm:inline-flex">
            <TabsTrigger value="rubrica" data-testid="tab-rubrica" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              Rubrica
            </TabsTrigger>
            <TabsTrigger value="ranking" data-testid="tab-ranking" className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Migliori T.O. per categoria
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rubrica" className="space-y-4 mt-0">
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <CardTitle>Tour Operator</CardTitle>
                <CardDescription>
                  Gestisci tutti i T.O. con cui collabori: contatti, accessi e destinazioni coperte.
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={handleExport}
                  data-testid="export-tour-operators-btn"
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Esporta Excel
                </Button>
                <Button
                  onClick={openCreate}
                  data-testid="create-tour-operator-btn"
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Nuovo T.O.
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-tour-operator-input"
                  placeholder="Cerca per nome, tipologia, destinazione, codice agenzia, email..."
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <Button type="submit" variant="outline" data-testid="search-tour-operator-btn">Cerca</Button>
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setSearch(''); fetchOperators(''); }}
                  data-testid="clear-search-btn"
                >
                  Pulisci
                </Button>
              )}
            </form>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : operators.length === 0 ? (
              <div className="text-center py-12 text-slate-500" data-testid="empty-state">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nessun Tour Operator in rubrica</p>
                <p className="text-sm">Clicca su "Nuovo T.O." per aggiungerne uno.</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Nome</TableHead>
                      <TableHead>Categorie / Rating</TableHead>
                      <TableHead>Destinazioni</TableHead>
                      <TableHead>Codice Ag.</TableHead>
                      <TableHead>Email Booking</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {operators.map((op) => (
                      <TableRow key={op.id} data-testid={`tour-operator-row-${op.id}`}>
                        <TableCell className="font-medium">{op.name}</TableCell>
                        <TableCell className="text-slate-600">
                          {(op.service_categories?.length || 0) > 0 ? (
                            <div className="flex flex-wrap gap-1 max-w-[260px]">
                              {op.service_categories.slice(0, 4).map((sc, idx) => {
                                const r = RATING_MAP[sc.rating] || RATING_MAP.good;
                                const Icon = r.icon;
                                return (
                                  <span
                                    key={`${sc.category}-${idx}`}
                                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${r.bg} ${r.color}`}
                                    title={`${sc.category} · ${r.label}`}
                                  >
                                    <Icon className="w-3 h-3" />
                                    {sc.category}
                                  </span>
                                );
                              })}
                              {op.service_categories.length > 4 && (
                                <span className="text-xs text-slate-400">
                                  +{op.service_categories.length - 4}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">{op.service_type || '—'}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-600 max-w-[220px] truncate" title={op.destinations}>
                          {op.destinations || '—'}
                        </TableCell>
                        <TableCell className="text-slate-600">{op.agency_code || '—'}</TableCell>
                        <TableCell className="text-slate-600">{op.booking_email || '—'}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewing(op)}
                              data-testid={`view-tour-operator-${op.id}`}
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(op)}
                              data-testid={`edit-tour-operator-${op.id}`}
                              title="Modifica"
                            >
                              <Pencil className="w-4 h-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteId(op.id)}
                              data-testid={`delete-tour-operator-${op.id}`}
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="ranking" className="space-y-4 mt-0">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-amber-500" />
                  Migliori Tour Operator per categoria
                </CardTitle>
                <CardDescription>
                  T.O. ordinati per rating (Eccellente → Buono → Da evitare) all'interno di ciascuna categoria
                </CardDescription>
              </CardHeader>
              <CardContent>
                {byCategory.length === 0 ? (
                  <div className="text-center py-12 text-slate-500" data-testid="ranking-empty">
                    <Star className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">Nessuna categoria valutata</p>
                    <p className="text-sm">Aggiungi categorie e rating ai T.O. dalla scheda di modifica.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {byCategory.map((cat) => (
                      <div
                        key={cat.category}
                        data-testid={`ranking-category-${cat.category}`}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                      >
                        <div className="bg-gradient-to-r from-blue-50 to-slate-50 px-4 py-2 flex items-center justify-between border-b border-slate-200">
                          <div className="flex items-center gap-2">
                            <Star className="w-4 h-4 text-blue-600" />
                            <span className="font-semibold text-slate-800">{cat.category}</span>
                            {!cat.is_predefined && (
                              <Badge variant="outline" className="text-[10px]">Custom</Badge>
                            )}
                          </div>
                          <Badge variant="secondary" className="text-xs">{cat.total}</Badge>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {cat.operators.map((op, idx) => {
                            const r = RATING_MAP[op.rating] || RATING_MAP.good;
                            const Icon = r.icon;
                            return (
                              <div
                                key={op.id}
                                className="flex items-center justify-between gap-2 px-4 py-2 hover:bg-slate-50"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-xs text-slate-400 w-5">#{idx + 1}</span>
                                  <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-800 truncate">
                                      {op.name}
                                    </div>
                                    {(op.agency_code || op.destinations) && (
                                      <div className="text-xs text-slate-500 truncate">
                                        {op.agency_code && `Cod. ${op.agency_code}`}
                                        {op.agency_code && op.destinations && ' · '}
                                        {op.destinations}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <span
                                  className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${r.bg} ${r.color}`}
                                  data-testid={`rating-${op.id}-${cat.category}`}
                                >
                                  <Icon className="w-3 h-3" />
                                  {r.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tour-operator-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Tour Operator' : 'Nuovo Tour Operator'}</DialogTitle>
            <DialogDescription>
              Tutti i campi sono modificabili. Solo il Nome è obbligatorio.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="md:col-span-2">
              <Label htmlFor="name">{FIELD_LABELS.name} *</Label>
              <Input
                id="name"
                data-testid="form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Costa Crociere"
              />
            </div>
            <div>
              <Label htmlFor="service_type">{FIELD_LABELS.service_type}</Label>
              <Input
                id="service_type"
                data-testid="form-service-type"
                value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                placeholder="Es. Crociere, Tour, Resort..."
              />
            </div>
            <div>
              <Label htmlFor="website">{FIELD_LABELS.website}</Label>
              <Input
                id="website"
                data-testid="form-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="destinations">{FIELD_LABELS.destinations}</Label>
              <Input
                id="destinations"
                data-testid="form-destinations"
                value={form.destinations}
                onChange={(e) => setForm({ ...form, destinations: e.target.value })}
                placeholder="Es. Caraibi, Mediterraneo, Asia..."
              />
            </div>

            {/* Categorie + Rating */}
            <div className="md:col-span-2 space-y-3 border border-slate-200 rounded-lg p-4 bg-slate-50/40">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Star className="w-4 h-4 text-blue-600" />
                  Categorie di Servizio + Rating
                </Label>
                <span className="text-xs text-slate-500">
                  {(form.service_categories || []).length} aggiunte
                </span>
              </div>

              {/* Existing categories list */}
              {(form.service_categories || []).length > 0 && (
                <div className="space-y-2" data-testid="form-categories-list">
                  {form.service_categories.map((sc, idx) => {
                    const r = RATING_MAP[sc.rating] || RATING_MAP.good;
                    const Icon = r.icon;
                    return (
                      <div
                        key={`${sc.category}-${idx}`}
                        className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2 py-1.5"
                        data-testid={`form-category-row-${idx}`}
                      >
                        <Icon className={`w-4 h-4 ${r.color} flex-shrink-0`} />
                        <span className="text-sm font-medium text-slate-800 flex-1 truncate">
                          {sc.category}
                        </span>
                        <Select
                          value={sc.rating}
                          onValueChange={(v) => updateCategoryRating(idx, v)}
                        >
                          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`form-category-rating-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {RATING_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => removeCategory(idx)}
                          data-testid={`form-category-remove-${idx}`}
                          className="text-slate-400 hover:text-red-500 p-1"
                          title="Rimuovi categoria"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Predefined categories chips */}
              {availablePredefined.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Aggiungi categoria predefinita:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {availablePredefined.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => addCategory(c)}
                        data-testid={`form-add-predef-${c}`}
                        className="text-xs px-2.5 py-1 rounded-full border border-slate-300 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700 transition"
                      >
                        + {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom category input */}
              <div>
                <p className="text-xs text-slate-500 mb-2">Oppure aggiungi una categoria personalizzata:</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Nome categoria custom..."
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCategory(newCategoryInput);
                      }
                    }}
                    data-testid="form-custom-category-input"
                    className="text-sm h-9"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addCategory(newCategoryInput)}
                    disabled={!newCategoryInput.trim()}
                    data-testid="form-add-custom-category-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="agency_code">{FIELD_LABELS.agency_code}</Label>
              <Input
                id="agency_code"
                data-testid="form-agency-code"
                value={form.agency_code}
                onChange={(e) => setForm({ ...form, agency_code: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="username">{FIELD_LABELS.username}</Label>
              <Input
                id="username"
                data-testid="form-username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="password">{FIELD_LABELS.password}</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="form-password"
                  type={revealedPasswords.form ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setRevealedPasswords((p) => ({ ...p, form: !p.form }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  data-testid="toggle-form-password"
                  title={revealedPasswords.form ? 'Nascondi' : 'Mostra'}
                >
                  {revealedPasswords.form ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="booking_contact_1">{FIELD_LABELS.booking_contact_1}</Label>
              <Input
                id="booking_contact_1"
                data-testid="form-booking-contact-1"
                value={form.booking_contact_1}
                onChange={(e) => setForm({ ...form, booking_contact_1: e.target.value })}
                placeholder="Nome / Telefono"
              />
            </div>
            <div>
              <Label htmlFor="booking_contact_2">{FIELD_LABELS.booking_contact_2}</Label>
              <Input
                id="booking_contact_2"
                data-testid="form-booking-contact-2"
                value={form.booking_contact_2}
                onChange={(e) => setForm({ ...form, booking_contact_2: e.target.value })}
                placeholder="Nome / Telefono"
              />
            </div>
            <div>
              <Label htmlFor="booking_email">{FIELD_LABELS.booking_email}</Label>
              <Input
                id="booking_email"
                data-testid="form-booking-email"
                type="email"
                value={form.booking_email}
                onChange={(e) => setForm({ ...form, booking_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="to_email">{FIELD_LABELS.to_email}</Label>
              <Input
                id="to_email"
                data-testid="form-to-email"
                type="email"
                value={form.to_email}
                onChange={(e) => setForm({ ...form, to_email: e.target.value })}
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="notes">{FIELD_LABELS.notes}</Label>
              <Textarea
                id="notes"
                data-testid="form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                placeholder="Note interne (opzionale)..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowForm(false)}
              disabled={saving}
              data-testid="cancel-tour-operator-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-tour-operator-btn"
            >
              {saving ? 'Salvataggio...' : (editingId ? 'Salva modifiche' : 'Crea Tour Operator')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tour-operator-view-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600" />
                  {viewing.name}
                </DialogTitle>
                <DialogDescription>{viewing.service_type || 'Tour Operator'}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
                {viewing.service_categories?.length > 0 && (
                  <div className="md:col-span-2">
                    <Label className="text-slate-500 text-xs flex items-center gap-1">
                      <Star className="w-3 h-3" /> Categorie + Rating
                    </Label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {viewing.service_categories.map((sc, idx) => {
                        const r = RATING_MAP[sc.rating] || RATING_MAP.good;
                        const Icon = r.icon;
                        return (
                          <span
                            key={`view-${sc.category}-${idx}`}
                            className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${r.bg} ${r.color}`}
                          >
                            <Icon className="w-3 h-3" />
                            <strong>{sc.category}</strong> · {r.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                <DetailField label={FIELD_LABELS.website} value={viewing.website} icon={<Globe className="w-4 h-4" />} isLink />
                <DetailField label={FIELD_LABELS.destinations} value={viewing.destinations} />
                <DetailField label={FIELD_LABELS.agency_code} value={viewing.agency_code} />
                <DetailField label={FIELD_LABELS.username} value={viewing.username} icon={<KeyRound className="w-4 h-4" />} />
                <div className="md:col-span-2">
                  <Label className="text-slate-500 text-xs">{FIELD_LABELS.password}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="bg-slate-100 px-3 py-1.5 rounded text-sm flex-1 font-mono">
                      {viewing.password
                        ? (revealedPasswords[viewing.id] ? viewing.password : '••••••••••')
                        : '—'}
                    </code>
                    {viewing.password && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => togglePassword(viewing.id)}
                        data-testid={`toggle-view-password-${viewing.id}`}
                      >
                        {revealedPasswords[viewing.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </div>
                <DetailField label={FIELD_LABELS.booking_contact_1} value={viewing.booking_contact_1} icon={<Phone className="w-4 h-4" />} />
                <DetailField label={FIELD_LABELS.booking_contact_2} value={viewing.booking_contact_2} icon={<Phone className="w-4 h-4" />} />
                <DetailField label={FIELD_LABELS.booking_email} value={viewing.booking_email} icon={<Mail className="w-4 h-4" />} isEmail />
                <DetailField label={FIELD_LABELS.to_email} value={viewing.to_email} icon={<Mail className="w-4 h-4" />} isEmail />
                {viewing.notes && (
                  <div className="md:col-span-2">
                    <Label className="text-slate-500 text-xs">{FIELD_LABELS.notes}</Label>
                    <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{viewing.notes}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setViewing(null)}
                  data-testid="close-view-btn"
                >
                  Chiudi
                </Button>
                <Button
                  onClick={() => { openEdit(viewing); setViewing(null); }}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="edit-from-view-btn"
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Modifica
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>
              Il Tour Operator verrà rimosso definitivamente dalla rubrica. L'azione non è reversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const DetailField = ({ label, value, icon, isLink, isEmail }) => {
  if (!value) {
    return (
      <div>
        <Label className="text-slate-500 text-xs">{label}</Label>
        <p className="mt-1 text-sm text-slate-400">—</p>
      </div>
    );
  }
  const content = isLink ? (
    <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
      {value}
    </a>
  ) : isEmail ? (
    <a href={`mailto:${value}`} className="text-blue-600 hover:underline break-all">{value}</a>
  ) : (
    <span className="text-slate-700">{value}</span>
  );
  return (
    <div>
      <Label className="text-slate-500 text-xs flex items-center gap-1">
        {icon}{label}
      </Label>
      <p className="mt-1 text-sm">{content}</p>
    </div>
  );
};

export default TourOperators;
